import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api/Api";
import toast from "react-hot-toast";
// ✅ Importing Icons for better UI
import { Plus, Edit2, Save, Filter } from "lucide-react"; 
import "bootstrap/dist/css/bootstrap.min.css"; 

const FeedbackSubcategoryListPage = () => {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");

  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editCategory, setEditCategory] = useState("");

  const loadData = async () => {
    try {
      const res = await api.get("/adminmod/feedback-subcategory-list");
      setItems(res.data.data || []);
    } catch (error) {
      toast.error("Failed to load data");
    }
  };

  const loadCategories = async () => {
    try {
      const res = await api.get("/adminmod/feedback-category-dropdown");
      setCategories(res.data.data || []);
    } catch (error) {
      console.error("Failed to load categories");
    }
  };

  useEffect(() => {
    loadData();
    loadCategories();
  }, []);

  const filteredItems = selectedCategory
    ? items.filter((i) => String(i.category_id) === String(selectedCategory))
    : items;

  // ✅ New Handler for Status Change with Toast
  const handleStatusChange = async (id, value) => {
    try {
      await api.put("/adminmod/feedback-subcategory-status", {
        id: id,
        is_active: value,
      });
      toast.success("Status updated"); // 🔔 Alert is now shown here
      loadData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const saveEdit = async (id) => {
    if (!editName || !editCategory) {
      toast.error("Category & Sub Category required");
      return;
    }

    try {
      await api.put("/adminmod/feedback-subcategory-edit", {
        id,
        item_names: editName,
        category_id: editCategory,
      });

      toast.success("Updated successfully");
      setEditId(null);
      loadData();
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  return (
    <div className="container-fluid py-3 bg-white min-vh-100">
      
      {/* MAIN CARD CONTAINER */}
      <div className="card shadow-sm border-0  rounded-3">
        
        {/* HEADER SECTION */}
        <div className="card-header bg-primary-subtle py-3">
          <div className="row g-3 align-items-center justify-content-between">
            
            {/* Title */}
            <div className="col-12 col-md-4">
              <h5 className="mb-0 fw-bold text-dark">Manage Feedback Sub Category</h5>
            </div>

            {/* Actions: Filter & Add Button */}
            <div className="col-12 col-md-8">
              <div className="d-flex flex-wrap justify-content-md-end gap-2">
                
                {/* Category Filter */}
                <div className="input-group input-group-sm" style={{ maxWidth: "280px", flex: "1 1 auto" }}>
                  <span className="input-group-text bg-light border-end-0 text-muted">
                    <Filter size={16} />
                  </span>
                  <select
                    className="form-select border-start-0 focus-ring-none"
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                  >
                    <option value="">-- Filter by Category --</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.item_names}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Add Button */}
                <Link
                  to="/master/feedback-subcategory-add"
                  className="btn btn-primary btn-sm d-flex align-items-center gap-2 px-3 fw-medium"
                >
                  <Plus size={16} /> Add New
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* TABLE SECTION */}
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover table-sm align-middle mb-0">
              <thead className="bg-light">
                <tr>
                  <th className="py-3 px-3 text-secondary text-uppercase small fw-bold" style={{ width: "5%" }}>#</th>
                  <th className="py-3 text-secondary text-uppercase small fw-bold" style={{ width: "30%" }}>Feedback Category</th>
                  <th className="py-3 text-secondary text-uppercase small fw-bold" style={{ width: "40%" }}>Feedback Sub Category</th>
                  <th className="py-3 text-center text-secondary text-uppercase small fw-bold" style={{ width: "10%" }}>Action</th>
                  <th className="py-3 text-center text-secondary text-uppercase small fw-bold" style={{ width: "15%" }}>Status</th>
                </tr>
              </thead>

              <tbody>
                {filteredItems.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center py-5 text-muted">
                      No records found.
                    </td>
                  </tr>
                ) : (
                  filteredItems.map((i, idx) => (
                    <tr key={i.id} className={editId === i.id ? "table-active" : ""}>
                      <td className="px-3 text-muted small">{idx + 1}</td>

                      {/* CATEGORY COLUMN */}
                      <td>
                        {editId === i.id ? (
                          <select
                            className="form-select form-select-sm"
                            value={editCategory}
                            onChange={(e) => setEditCategory(e.target.value)}
                          >
                            <option value="">Select Category</option>
                            {categories.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.item_names}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className="badge bg-light text-dark border">
                            {i.category_name}
                          </span>
                        )}
                      </td>

                      {/* SUB CATEGORY COLUMN */}
                      <td>
                        {editId === i.id ? (
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            autoFocus
                          />
                        ) : (
                          <span className="fw-medium text-dark">{i.item_names}</span>
                        )}
                      </td>

                      {/* ACTION COLUMN */}
                      <td className="text-center">
                        {editId === i.id ? (
                          <button
                            className="btn btn-success btn-sm d-inline-flex align-items-center gap-1"
                            onClick={() => saveEdit(i.id)}
                            title="Save"
                          >
                            <Save size={14} /> Save
                          </button>
                        ) : (
                          <button
                            className="btn btn-light btn-sm text-primary border-0 hover-shadow"
                            onClick={() => {
                              setEditId(i.id);
                              setEditName(i.item_names);
                              setEditCategory(i.category_id);
                            }}
                            title="Edit"
                          >
                            <Edit2 size={16} />
                          </button>
                        )}
                      </td>

                      {/* STATUS COLUMN (Updated) */}
                      <td className="text-center">
                        <select
                          className={`form-select form-select-sm fw-bold border-0 ${
                            i.is_active == 1 ? "text-success bg-success-subtle" : "text-danger bg-danger-subtle"
                          }`}
                          style={{ width: "110px", margin: "0 auto", cursor: "pointer" }}
                          value={i.is_active}
                          onChange={(e) => handleStatusChange(i.id, e.target.value)}
                        >
                          <option value="1">Active</option>
                          <option value="0">Inactive</option>
                        </select>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Footer Info */}
        <div className="card-footer bg-white py-3">
           <small className="text-muted">Showing {filteredItems.length} records</small>
        </div>

      </div>
    </div>
  );
};

export default FeedbackSubcategoryListPage;